import { useMemo, useState } from 'react';
import { calmTokens as t } from '../../../shared/tokens';
import { fmtEUR, fmtDateShort } from '../../../shared/format';
import { IMPORT_ACTION_KINDS, LABEL_STATUS_KINDS, REVIEW_STATUSES } from '../../../shared/labels';
import { Chip, PillButton } from './ui';
import { inputStyle, monoStyle } from './styles';

const GRID = '64px minmax(140px, 1.2fr) 96px 110px minmax(200px, 1.6fr) 130px';

function Header() {
  const cell = (label, extra = {}) => (
    <div style={{ fontSize: 10.5, color: t.textMute, textTransform: 'uppercase', letterSpacing: 0.8, ...extra }}>{label}</div>
  );
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 12,
        padding: '8px 16px',
        borderBottom: `1px solid ${t.border}`,
        alignItems: 'center',
      }}
    >
      {cell('Date')}
      {cell('Banque')}
      {cell('Montant', { textAlign: 'right' })}
      {cell('Action')}
      {cell('Libellé dans l\'app')}
      {cell('Statut')}
    </div>
  );
}

function Row({ row, edit, onEdit }) {
  const isDebit = row.type === 'debit';
  const actionKind = IMPORT_ACTION_KINDS[row.action];
  const editable = row.action === 'create';
  const status = edit ? 'confirmed' : row.labelStatus;
  const statusKind = status ? LABEL_STATUS_KINDS[status] : null;
  const label = edit ? edit.label : row.label ?? '';
  const emoji = edit ? edit.emoji : row.emoji ?? '';
  const review = !edit && REVIEW_STATUSES.has(row.labelStatus) && editable;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 12,
        padding: '10px 16px',
        alignItems: 'center',
        borderBottom: `1px solid ${t.border}`,
        background: review ? 'rgba(253,224,71,0.03)' : 'transparent',
        opacity: row.action === 'duplicate' ? 0.55 : 1,
      }}
    >
      <div style={{ fontSize: 12, color: t.textDim, ...monoStyle }}>{fmtDateShort(row.date)}</div>
      <div style={{ minWidth: 0 }}>
        <div
          title={row.bankRawLabel}
          style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {row.bankLabel}
        </div>
        <div style={{ fontSize: 10.5, color: t.textMute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.bankType}
        </div>
      </div>
      <div style={{ textAlign: 'right', fontSize: 13, color: isDebit ? t.debit : t.credit, ...monoStyle }}>
        {isDebit ? '− ' : '+ '}
        {fmtEUR(row.amount)}
      </div>
      <div>
        <Chip kind={actionKind}>{row.action === 'recurring' && row.recurringTemplates.length > 1 ? `${actionKind.label} ×${row.recurringTemplates.length}` : (actionKind?.label || row.action)}</Chip>
      </div>
      <div style={{ minWidth: 0 }}>
        {editable ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              aria-label="Emoji"
              value={emoji}
              onChange={(e) => onEdit(row.rowIndex, { label, emoji: e.target.value })}
              placeholder="🙂"
              style={{ ...inputStyle, width: 44, textAlign: 'center', padding: 0 }}
            />
            <input
              aria-label="Libellé"
              value={label}
              onChange={(e) => onEdit(row.rowIndex, { label: e.target.value, emoji })}
              style={{ ...inputStyle, flex: 1 }}
            />
          </div>
        ) : row.action === 'recurring' ? (
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {row.recurringTemplates.map((tpl) => (
              <div key={tpl.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.title}</span>
                <span style={{ color: t.textMute, ...monoStyle, fontSize: 12 }}>{fmtEUR(tpl.amount)}</span>
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: t.textMute }}>
              {row.recurringTemplates.length > 1 ? `${row.recurringTemplates.length} récurrentes, une opération chacune` : 'récurrente reconnue par son montant'}
            </div>
          </div>
        ) : row.action === 'matched' ? (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.matchedTransaction?.description || row.description}
            </div>
            <div style={{ fontSize: 10.5, color: t.textMute }}>
              saisie le {row.matchedTransaction?.date ? fmtDateShort(row.matchedTransaction.date) : '?'} · sera pointée banque
            </div>
          </div>
        ) : (
          <span style={{ fontSize: 12, color: t.textMute }}>déjà dans l’app</span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
        {statusKind && <Chip kind={statusKind}>{statusKind.label}</Chip>}
        {editable && !edit && row.confidence > 0 && row.labelStatus !== 'rule' && (
          <span style={{ fontSize: 10, color: t.textMute, ...monoStyle }}>
            {Math.round(row.confidence * 100)} %
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Aperçu d'import : lignes éditables (libellé emoji + texte) avant validation.
 * edits : Map rowIndex → { label, emoji }
 */
export default function ImportPreviewTable({ rows, edits, onEdit, onReset }) {
  const [onlyReview, setOnlyReview] = useState(false);
  const reviewCount = useMemo(
    () => rows.filter((r) => r.action === 'create' && REVIEW_STATUSES.has(r.labelStatus) && !edits.has(r.rowIndex)).length,
    [rows, edits]
  );
  const visible = useMemo(
    () => (onlyReview ? rows.filter((r) => r.action === 'create' && (REVIEW_STATUSES.has(r.labelStatus) || edits.has(r.rowIndex))) : rows),
    [rows, onlyReview, edits]
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <PillButton small primary={onlyReview} onClick={() => setOnlyReview((v) => !v)}>
          {onlyReview ? 'Tout afficher' : `À vérifier (${reviewCount})`}
        </PillButton>
        {edits.size > 0 && (
          <PillButton small onClick={onReset}>
            Annuler mes {edits.size} modification{edits.size > 1 ? 's' : ''}
          </PillButton>
        )}
        <span style={{ fontSize: 11, color: t.textMute, marginLeft: 'auto' }}>
          Modifie un libellé directement dans la ligne : il sera enregistré tel quel et servira d’exemple.
        </span>
      </div>
      <div
        style={{
          border: `1px solid ${t.border}`,
          borderRadius: 14,
          overflow: 'hidden',
          background: t.bg,
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 820 }}>
            <Header />
            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
              {visible.map((row) => (
                <Row key={row.rowIndex} row={row} edit={edits.get(row.rowIndex)} onEdit={onEdit} />
              ))}
              {visible.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: t.textMute }}>
                  Rien à afficher.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
